/**
 * EVERFORGE Automatic Country & Region Detection Service
 *
 * Requirements:
 * - Detect visitor's country automatically without visitor interaction.
 * - Country codes: 'US', 'IN', 'UNSUPPORTED'.
 * - No country/currency switcher anywhere in the UI.
 * - Cloudflare Worker / Edge compatible (reads request.cf.country or /api/detectCountry).
 * - Safe server-side fallback and local preview support.
 * - In-memory caching: cached per session so country detection executes exactly once.
 */

export type MarketCode = 'US' | 'IN' | 'UNSUPPORTED';

export interface CountryDetectionResult {
  countryCode: string; // ISO 2-letter, e.g. 'US', 'IN', 'GB', etc.
  marketCode: MarketCode;
  source: 'cloudflare-worker' | 'edge-endpoint' | 'ip-api-fallback' | 'local-preview' | 'fallback';
}

let cachedDetection: CountryDetectionResult | null = null;

/**
 * Normalizes ISO country code to EVERFORGE Market Code: 'US', 'IN', or 'UNSUPPORTED'
 */
export function normalizeCountryToMarket(countryCode?: string | null): MarketCode {
  if (!countryCode) return 'UNSUPPORTED';
  const clean = countryCode.trim().toUpperCase();
  if (clean === 'US') return 'US';
  if (clean === 'IN') return 'IN';
  return 'UNSUPPORTED';
}

/**
 * Detects visitor country automatically.
 * Priority order:
 * 1. In-memory session cache.
 * 2. Same-origin Cloudflare Worker / Server endpoint: /api/detectCountry
 * 3. Safe fallback HTTPS geolocation endpoint: Cloudflare trace / ipapi.co (no raw IP storage)
 * 4. Fails safely to 'UNSUPPORTED' (Contact for pricing)
 */
export async function detectVisitorMarket(): Promise<CountryDetectionResult> {
  if (cachedDetection) {
    return cachedDetection;
  }

  // Check if running in SSR / non-browser
  if (typeof window === 'undefined') {
    return { countryCode: 'US', marketCode: 'US', source: 'local-preview' };
  }

  // 1. Attempt same-origin /api/detectCountry (Cloudflare Worker or Hosting function)
  try {
    const res = await fetch('/api/detectCountry', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const data = await res.json();
      if (data && data.country) {
        const country = String(data.country).toUpperCase();
        cachedDetection = {
          countryCode: country,
          marketCode: normalizeCountryToMarket(country),
          source: 'cloudflare-worker',
        };
        return cachedDetection;
      }
    }
  } catch {
    // Continue to fallback
  }

  // 2. Cloudflare trace edge check (https://cloudflare.com/cdn-cgi/trace or direct cdn-cgi on domain)
  try {
    const cfRes = await fetch('/cdn-cgi/trace', { method: 'GET' });
    if (cfRes.ok) {
      const text = await cfRes.text();
      const locMatch = text.match(/loc=([A-Z]{2})/i);
      if (locMatch && locMatch[1]) {
        const country = locMatch[1].toUpperCase();
        cachedDetection = {
          countryCode: country,
          marketCode: normalizeCountryToMarket(country),
          source: 'edge-endpoint',
        };
        return cachedDetection;
      }
    }
  } catch {
    // Continue to external edge check
  }

  // 3. Fallback external edge check: Cloudflare 1.1.1.1 trace (no IP storage, returns plain text loc=XX)
  try {
    const traceRes = await fetch('https://1.1.1.1/cdn-cgi/trace', { method: 'GET' });
    if (traceRes.ok) {
      const text = await traceRes.text();
      const locMatch = text.match(/loc=([A-Z]{2})/i);
      if (locMatch && locMatch[1]) {
        const country = locMatch[1].toUpperCase();
        cachedDetection = {
          countryCode: country,
          marketCode: normalizeCountryToMarket(country),
          source: 'ip-api-fallback',
        };
        return cachedDetection;
      }
    }
  } catch {
    // Continue to safe default
  }

  // 4. Default safe fallback: UNSUPPORTED (neutral contact for pricing, no assumption)
  cachedDetection = {
    countryCode: 'UNKNOWN',
    marketCode: 'UNSUPPORTED',
    source: 'fallback',
  };
  return cachedDetection;
}

/**
 * Resets the in-memory detection cache (for automated unit testing only)
 */
export function _resetDetectionCache(): void {
  cachedDetection = null;
}

